// src/lib/discussions/inbox.ts
//
// The Discussions inbox: every thread you can see with activity, newest
// first, each row previewing the last message and whether it's waiting on you.
// Pure so the page and the sidebar badge derive from the same rule.

import { isUnread } from './unread'
import { discussionState, memberOfAuth, othersIn, type DiscussionState, type WaitingMember } from './waiting'

export type InboxEntityType = 'task' | 'routine' | 'event'

export interface InboxSession {
  id: string
  entity_type: string | null
  entity_id: string | null
  title: string | null
  messages: unknown
  updated_at: string
  scope?: string | null
  user_id?: string | null
  with_user_ids?: string[] | null
  settled_at?: string | null
  settled_by?: string | null
  outcome?: string | null
}

export interface InboxRow {
  sessionId: string
  entityType: InboxEntityType
  entityId: string
  title: string
  lastAuthor: string
  lastText: string
  lastAt: Date
  unread: boolean
  /** Whose turn it is (discussions v2). */
  state: DiscussionState
  /** The others in it, by name ("Iris"). */
  others: string[]
  /** Whose turn, by name whoever is looking ("Iris", or your own name) — the
   *  wall's view, which belongs to no one in the room. */
  waitingOn: string[]
  scope: string
  settledAt: Date | null
  settledBy: string | null
  outcome: string | null
}

interface StoredMessage {
  role?: string
  content?: string
  timestamp?: string
  author?: { id?: string | null; name?: string; kind?: string }
}

function readMessages(raw: unknown): StoredMessage[] {
  return Array.isArray(raw) ? (raw as StoredMessage[]) : []
}

/** One-line preview: markdown markers and line breaks read as noise in a list row. */
function previewText(raw: string): string {
  return raw.replace(/[*_`#>]+/g, '').replace(/\s+/g, ' ').trim()
}

function isEntityType(t: string | null): t is InboxEntityType {
  return t === 'task' || t === 'routine' || t === 'event'
}

/**
 * @param reads  session id → ISO last_read_at for the viewer
 */
export function buildInboxRows(
  sessions: readonly InboxSession[],
  reads: Readonly<Record<string, string>>,
  selfAuthId: string | null,
  members: readonly WaitingMember[] = [],
): InboxRow[] {
  const rows: InboxRow[] = []
  for (const s of sessions) {
    if (!isEntityType(s.entity_type) || !s.entity_id) continue
    const msgs = readMessages(s.messages).filter((m) => (m.content ?? '').trim().length > 0)
    const last = msgs[msgs.length - 1]
    if (!last) continue
    const lastAt = last.timestamp ? new Date(last.timestamp) : new Date(s.updated_at)
    const readable = msgs.map((m) => ({
      timestamp: m.timestamp ? new Date(m.timestamp) : new Date(0),
      author: {
        id: typeof m.author?.id === 'string' ? m.author.id : null,
        kind: (m.role === 'assistant' || m.author?.kind === 'symphony' ? 'symphony' : 'member') as 'member' | 'symphony',
      },
    }))
    const readAt = reads[s.id] ? new Date(reads[s.id]) : null
    rows.push({
      sessionId: s.id,
      entityType: s.entity_type,
      entityId: s.entity_id,
      title: s.title?.trim() || 'Untitled',
      lastAuthor: last.role === 'assistant' ? 'Symphony' : (last.author?.name ?? 'Someone'),
      lastText: previewText(last.content ?? ''),
      lastAt,
      unread: isUnread(readable, selfAuthId, readAt),
      ...(() => {
        const ws = { user_id: s.user_id ?? '', scope: s.scope, with_user_ids: s.with_user_ids, settled_at: s.settled_at, messages: s.messages }
        const state: DiscussionState = s.user_id ? discussionState(ws, members, selfAuthId) : { kind: 'just-you' }
        const selfName = memberOfAuth(members, selfAuthId)?.name
        return {
          state,
          others: s.user_id ? othersIn(ws, members, selfAuthId) : [],
          waitingOn: state.kind === 'waiting-on-them' ? state.names : state.kind === 'waiting-on-you' && selfName ? [selfName] : [],
        }
      })(),
      scope: s.scope ?? 'individual',
      settledAt: s.settled_at ? new Date(s.settled_at) : null,
      settledBy: s.settled_by ?? null,
      outcome: s.outcome?.trim() || null,
    })
  }
  rows.sort((a, b) => b.lastAt.getTime() - a.lastAt.getTime())
  return rows
}
